/**
 * The season: what it says, where it dresses, how it looks, and when.
 *
 * Everything seasonal hangs off SEASON below, so going into a season and out
 * of it is a change here and a deploy:
 *
 *   - the dates, and which routes are dressed;
 *   - the card, shown once on each device and kept under "What's new";
 *   - `decor`, which names the props' drawer in src/render/seasonal.ts;
 *   - `art`, which names the icons and title logo made by
 *     tools/make-icons.mjs into public/seasons/<art>/.
 *
 * Outside its dates a season shows nothing: the props and the card go, and
 * the Worker serves the year-round icons and logo again. A season left in
 * place switches itself off.
 *
 * Shared by the game and the Worker, so DOM-free. Presentation only: nothing
 * here reaches the sim, and props can come and go without touching a score.
 */

export interface SeasonNews {
  head: string;
  body: string;
}

export interface Season {
  /** Unique per season and year: a device that has seen this card is not shown it again. */
  id: string;
  /** Which props' drawer, see src/render/seasonal.ts. */
  decor: 'halloween';
  /** Which icons and title logo, from public/seasons/<art>/. */
  art: string;
  /** Dates, YYYY-MM-DD: on from the first, off from the second. */
  from: string;
  until: string;
  /** Routes dressed for the season, by id. */
  routes: string[];
  greeting: {
    kicker: string;
    title: string;
    lede: string;
    news: SeasonNews[];
    button: string;
  };
}

export const SEASON: Season | null = {
  id: 'halloween-2026',
  decor: 'halloween',
  art: 'halloween',
  from: '2026-10-01',
  until: '2026-12-01',
  routes: ['akari-downhill'],
  greeting: {
    kicker: 'Happy Halloween',
    title: 'Night falls on Akari',
    lede: 'Lanterns are lit along Akari Downhill, and something circles the hairpins. Drive it before they go, at the end of November.',
    news: [
      {
        head: 'Categories',
        body: 'Every car now races in GT, SPORT or STREET, set by its real pace. Boards open on your car’s category, so the Hachiroku finally has a fair fight. It got a tune-up too.',
      },
      {
        head: 'Speed counts',
        body: 'Drift points are paid by the metre, and fast metres pay more. Crawling into corners no longer does. (Sorry Josef).',
      },
      {
        head: 'Fresh boards',
        body: 'Every leaderboard starts empty. The top spots are anyone’s.',
      },
    ],
    button: 'Let’s drift',
  },
};

/**
 * The files a season replaces, as the page asks for them. The Worker serves
 * each from public/seasons/<art>/ while the season is on, and
 * tools/make-icons.mjs writes every one of them there.
 */
export const SEASONAL_FILES = [
  '/favicon-16.png',
  '/favicon-32.png',
  '/favicon-48.png',
  '/apple-touch-icon.png',
  '/icon-192.png',
  '/icon-512.png',
  '/icon-maskable-512.png',
  '/logo-title.png',
  '/logo-title.webp',
];

/** YYYY-MM-DD for a date: local time in the game, UTC on the Worker. */
export function dayOf(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** The season on this date, or null. */
export function seasonOn(date: Date): Season | null {
  if (!SEASON) return null;
  const day = dayOf(date);
  return day >= SEASON.from && day < SEASON.until ? SEASON : null;
}
